import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';

const SERVICES = [
	{
		title: 'Kitchen Remodeling',
		img: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?q=80&w=900&auto=format&fit=crop',
		desc: 'A kitchen remodel is one of the highest-return investments you can make in your home. Our team handles complete kitchen transformations — from layout redesign and plumbing relocation to new cabinets, countertops, backsplash tile, lighting, and appliance installation.',
		bullets: [
			'Custom and semi-custom cabinetry',
			'Quartz, granite, and solid-surface countertops',
			'Island additions and peninsula builds',
			'Under-cabinet and pendant lighting',
			'Full plumbing and electrical coordination',
		],
	},
	{
		title: 'Bathroom Renovation',
		img: 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?q=80&w=900&auto=format&fit=crop',
		desc: 'From a quick vanity upgrade to a full primary suite bathroom gut-and-rebuild, we specialize in creating bathroom spaces that are both beautiful and functional. We handle tile, plumbing, fixtures, lighting, ventilation, and waterproofing.',
		bullets: [
			'Walk-in shower and tub-to-shower conversions',
			'Freestanding soaking tub installation',
			'Custom tile work — large format, mosaic, accent',
			'Vanity, fixture, and hardware installation',
			'Heated flooring and ventilation upgrades',
		],
	},
	{
		title: 'Flooring & Tile',
		img: 'https://images.unsplash.com/photo-1615873968403-89e068629265?q=80&w=900&auto=format&fit=crop',
		desc: 'Whether you\'re replacing worn-out carpet, upgrading to hardwood, or installing large-format tile throughout the main level, our flooring team delivers clean, precise results. We work with all flooring types and handle all subfloor preparation.',
		bullets: [
			'Solid and engineered hardwood',
			'Luxury vinyl plank (LVP) and tile (LVT)',
			'Ceramic and porcelain tile',
			'Natural stone — travertine, slate, marble',
			'Carpet installation and removal',
		],
	},
	{
		title: 'Interior & Exterior Painting',
		img: 'https://images.unsplash.com/photo-1562259929-b4e1fd3aef09?q=80&w=900&auto=format&fit=crop',
		desc: 'Our painting crews are neat, detail-oriented, and use premium paints from Sherwin-Williams and Benjamin Moore. We prep every surface properly — patching, sanding, priming — before a drop of color goes on the wall.',
		bullets: [
			'Full interior painting — every room and surface',
			'Exterior painting — stucco, wood, brick, siding',
			'Cabinet refinishing and repainting',
			'Accent walls and specialty finishes',
			'Color consultation available',
		],
	},
	{
		title: 'Windows & Doors',
		img: 'https://images.unsplash.com/photo-1527030280862-64139fba04ca?q=80&w=900&auto=format&fit=crop',
		desc: 'New windows and doors improve your home\'s energy efficiency, security, comfort, and curb appeal simultaneously. We install energy-efficient double and triple-pane windows and a full range of entry, patio, and sliding glass doors.',
		bullets: [
			'Vinyl, fiberglass, and aluminum window frames',
			'Energy Star certified window replacement',
			'Entry door, storm door, and screen door installation',
			'Sliding and folding patio glass doors',
			'Proper flashing, sealing, and insulation',
		],
	},
	{
		title: 'Decks & Patios',
		img: 'https://images.unsplash.com/photo-1591825729269-caeb344f6df2?q=80&w=900&auto=format&fit=crop',
		desc: 'In Arizona, outdoor living is year-round living. We design and build custom decks, covered patios, pergolas, shade structures, and outdoor kitchens that complement your home and stand up to the desert climate.',
		bullets: [
			'Pressure-treated wood and composite decking',
			'Custom pergolas and shade sail structures',
			'Covered patio additions',
			'Concrete and paver patio surfaces',
			'Outdoor kitchen and BBQ island builds',
		],
	},
	{
		title: 'General Home Renovation',
		img: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?q=80&w=900&auto=format&fit=crop',
		desc: 'Not every project fits a single category. We handle full-home renovation projects, room additions, garages, laundry rooms, home office conversions, and more. If it involves improving your home, A-Z Home Improvement can do it.',
		bullets: [
			'Room additions and garage conversions',
			'Laundry and mudroom renovations',
			'Drywall, texturing, and trim work',
			'Interior demolition and framing',
			'Permit assistance and coordination',
		],
	},
];

function Services() {
	return (
		<>
			<Helmet>
				<title>Home Remodeling Services | A-Z Home Improvement LLC | Phoenix, AZ</title>
				<meta name="description" content="Kitchen remodels, bathroom renovations, flooring, painting, windows, decks, and more. A-Z Home Improvement LLC serves the Phoenix metro area. Free estimates." />
			</Helmet>
			<PageHeader
				title="Home Improvement Services"
				subtitle="Full-service remodeling and renovation for Phoenix-area homeowners — from a single room to a whole-home transformation."
			/>

			{/* Intro */}
			<section className="section">
				<div className="container" style={{ maxWidth: 740 }}>
					<h2 style={{ textAlign: 'center' }}>Every Service Under One Roof</h2>
					<p className="helper" style={{ textAlign: 'center', maxWidth: 560, margin: '0 auto 16px' }}>
						A-Z Home Improvement handles your project from start to finish — design planning, material selection, permitting, installation, and cleanup. No juggling multiple contractors.
					</p>
				</div>
			</section>

			{/* Service detail blocks */}
			{SERVICES.map((s, i) => (
				<section key={s.title} className={`section ${i % 2 === 0 ? 'section-alt' : 'section-light'}`}>
					<div className={`container two-col ${i % 2 !== 0 ? 'two-col-reverse' : ''}`} style={{ gap: 36, alignItems: 'center' }}>
						<div className="img-frame fade-up">
							<img src={s.img} alt={s.title} />
						</div>
						<div className="fade-up" style={{ animationDelay: '80ms' }}>
							<h2 style={{ marginTop: 0 }}>{s.title}</h2>
							<p className="helper">{s.desc}</p>
							<ul style={{ margin: '16px 0 20px', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
								{s.bullets.map(b => (
									<li key={b} className="check-item" style={{ gap: 8 }}>
										<div className="check-icon" style={{ flexShrink: 0, width: 22, height: 22, fontSize: 12 }}>✓</div>
										<span style={{ fontSize: 14 }}>{b}</span>
									</li>
								))}
							</ul>
							<Link to="/contact" className="btn">Get a Free Estimate</Link>
						</div>
					</div>
				</section>
			))}

			{/* Warranty */}
			<section className="section section-alt">
				<div className="container" style={{ maxWidth: 680, textAlign: 'center' }}>
					<div style={{ fontSize: 40, marginBottom: 10 }}>🛡️</div>
					<h2>5-Year Workmanship Warranty</h2>
					<p className="helper">Every service we provide is backed by our 5-year workmanship warranty. If something fails due to our installation or craftsmanship within five years of project completion, we return to fix it at no charge. Materials are covered under manufacturer warranties, which we help you register and manage.</p>
					<Link to="/contact" className="btn" style={{ fontSize: 15, padding: '12px 28px', marginTop: 16, display: 'inline-block' }}>Schedule a Consultation</Link>
				</div>
			</section>
		</>
	);
}

export default Services;
